import { useQuery } from "@apollo/client/react";
import { cx } from "@emotion/css";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons/faArrowDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStore } from "@nanostores/react";
import { useCallback, useState } from "react";
import Card from "react-bootstrap/esm/Card";
import { useTranslation } from "react-i18next";
import { $favoriteIds, isFavorite, toggleFavorite } from "../stores/favorites";
import {
  type CampsiteFilter as CampsiteFilterInput,
  GetCampsiteDocument,
  GetCampsitesDocument,
} from "../types/graphql/graphql";
import { CampsiteCard } from "./CampsiteCard";
import { CampsiteFilter } from "./CampsiteFilter";
import styles from "./CampsiteIndex.module.css";
import { CampsitesMap } from "./CampsitesMap";

export default function CampsiteIndex() {
  const hash = window.location.hash.slice(1);
  const [filter, setFilter] = useState<CampsiteFilterInput>();
  const [expandedCard, setExpandedCard] = useState<string | undefined>(hash ? hash : undefined);
  const [mapIsMoving, setMapIsMoving] = useState<boolean>(false);
  const [visibleCampsiteIds, setVisibleCampsiteIds] = useState<number[] | undefined>(undefined);
  const { data: campsitesData } = useQuery(GetCampsitesDocument, { variables: { filter } });
  const { data: campsitesDetailData, loading } = useQuery(GetCampsiteDocument, {
    variables: { ref: expandedCard },
    skip: !expandedCard,
  });
  const { t } = useTranslation();
  const campsiteDetail = campsitesDetailData?.campsite ?? undefined;
  const favoriteIds = useStore($favoriteIds);
  const handleOnExpand = (ref: string) => {
    if (ref !== "" && ref !== expandedCard) {
      setExpandedCard(ref);
      window.location.hash = `#${ref}`;
    } else {
      setExpandedCard(undefined);
      window.location.hash = "";
    }
  };
  const handleFilter = useCallback((filterInput: CampsiteFilterInput) => {
    setExpandedCard(undefined);
    setFilter(filterInput);
    window.location.hash = "";
  }, []);

  return (
    <div className="bg-white">
      <div id="search" className="bg-glass">
        <section className="container py-4">
          <CampsiteFilter onFilter={handleFilter} />
        </section>
      </div>
      <section className="container" id="map" style={{ scrollMarginTop: "6rem" }}>
        <CampsitesMap
          campsitesData={campsitesData}
          onExpand={handleOnExpand}
          onMove={setMapIsMoving}
          onVisibleMarkersChanged={setVisibleCampsiteIds}
        />
      </section>
      <section className={cx(styles.campsContainer, "container", "py-5", { "opacity-50": mapIsMoving })}>
        {campsitesData?.campsites.flatMap((campsite) => {
          if (visibleCampsiteIds && !visibleCampsiteIds.includes(campsite.id)) return null;
          const isCurrentlyExpanded = expandedCard === campsite.ref;

          return (
            <>
              {isCurrentlyExpanded && (
                <Card key={`${campsite.id}-expanded`} className={cx(styles.card, styles.placeholder)}>
                  <button type="button" className="btn" onClick={() => handleOnExpand(campsite.ref)}>
                    {t("public.campsites.campsite.expanded")}
                    <FontAwesomeIcon icon={faArrowDown} />
                  </button>
                </Card>
              )}
              <CampsiteCard
                key={campsite.ref}
                campsite={campsite}
                details={isCurrentlyExpanded ? campsiteDetail : undefined}
                detailsLoading={isCurrentlyExpanded ? loading : undefined}
                onExpand={handleOnExpand}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite(campsite.id, favoriteIds)}
              />
            </>
          );
        })}
      </section>
    </div>
  );
}
